import React, { useState } from 'react';
import { CenteredSpinner } from "../Common/CenteredSpinner";
import { Container, Row, Table } from 'reactstrap';
import { Button } from 'react-bootstrap';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import FilterInput, { textSatisfiesFilter } from '../FilterInput';
import Confirm from '../Common/Confirm';
import { StaticSiteFilterPill } from '../UserAccess/SiteScope/SiteFilterPill';
import { ActionsColumnHeader } from '../Common/ActionsColumn';
import formatPostalAddress from '../Common/PostalAddressDisplay';
import { modalMode } from '../Common/EntityModal';
import PermissionButton from '../UserAccess/PermissionButton';
import EditViewButton from '../UserAccess/EditViewButton';
import { usePermission } from '../UserAccess/usePermission';
import { customerDisplayName } from '../../Utilities';
import useGetSuppliers, { SupplierDto } from '../Requests/Suppliers/useGetSuppliers';
import useRemoveSupplier from '../Requests/Suppliers/useRemoveSupplier';
import { CreateEditSupplier } from './CreateEditSupplier';

const alphabeticallyByName = (a: SupplierDto, b: SupplierDto) => a.name.localeCompare(b.name);

const SupplierPage = () => {
  const displayMessage = useDisplayMessage();
  const [filterText, setFilterText] = useState('');
  const [createEditSupplier, setCreateEditSupplier] = useState<{ supplier?: SupplierDto } | undefined>(undefined);
  const [supplierToRemove, setSupplierToRemove] = useState<SupplierDto | undefined>(undefined);
  const canModify = usePermission('otherEntitiesModify');

  const getSuppliers = useGetSuppliers();

  const removeSupplier = useRemoveSupplier(supplierToRemove?.id, {
    onSuccess: () => {
      displayMessage.success(`Supplier ${supplierToRemove?.name} removed`);
      getSuppliers.query();
    },
    onError: err => displayMessage.fail(err.message),
    onComplete: () => setSupplierToRemove(undefined),
  });

  const containsFilterText = (supplier: SupplierDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(supplier.name) || satisfiesFilter(supplier.accountNumber);
  };

  const formatSupplierPostalAddress = (supplier: SupplierDto) =>
    formatPostalAddress(
      supplier.addressStreet,
      supplier.addressCity,
      supplier.addressState,
      supplier.addressPostalCode,
      supplier.addressCountry);

  const visibleSuppliers = (getSuppliers.data ?? [])
    .filter(containsFilterText)
    .sort(alphabeticallyByName);

  const loadingRows = getSuppliers.isLoading || removeSupplier.isLoading;

  if (getSuppliers.isError) return <h3>{getSuppliers.error?.message}</h3>

  return <Container>
    {!!createEditSupplier && <CreateEditSupplier
      initSupplier={createEditSupplier.supplier}
      mode={modalMode(canModify, !createEditSupplier.supplier)}
      onHide={() => setCreateEditSupplier(undefined)}
      onSuccess={supplier => {
        const wasEdit = !!createEditSupplier.supplier;
        setCreateEditSupplier(undefined);
        displayMessage.success(`Supplier ${supplier.name} ${wasEdit ? 'updated' : 'created'}`);
        getSuppliers.query();
      }} />}
    <Confirm
      visible={!!supplierToRemove}
      title='Remove supplier'
      body={`When a supplier is removed it cannot be restored.
             Are you sure you want to remove ${supplierToRemove?.name}?`}
      isLoading={removeSupplier.isLoading}
      onDismiss={() => setSupplierToRemove(undefined)}
      onConfirm={() => removeSupplier.request()} />
    <Row className='justify-content-between mb-2'>
      <h2 className='col-auto ka-blue'>Suppliers</h2>
      <PermissionButton
        permission="otherEntitiesModify"
        className='col-auto btn-ghost-primary'
        onClick={() => setCreateEditSupplier({ supplier: undefined })}>
        Create Supplier
      </PermissionButton>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <form>
      <div className='row gx-2 gx-md-3 mb-3'>
        <div className='col-md-4 mb-2 mb-md-0'>
          <FilterInput placeholder='Search by name or number' onFilterTextChange={setFilterText} value={filterText} />
        </div>
        <div className='col-auto d-flex align-items-center'>
          <StaticSiteFilterPill entities="Suppliers" />
        </div>
      </div>
    </form>
    <Table bordered className="preserve-whitespace" style={{tableLayout: "fixed"}}>
      <thead>
        <tr>
          <th className="col-3">Supplier</th>
          <th>Address</th>
          <ActionsColumnHeader />
        </tr>
      </thead>
      <tbody>
        {!loadingRows && visibleSuppliers.map(supplier =>
          <tr key={supplier.id}>
            <td>{customerDisplayName(supplier.name, supplier.accountNumber)}</td>
            <td>{formatSupplierPostalAddress(supplier)}</td>
            <td align="right" className='text-nowrap'>
              <div className='dropdown'>
                <Button id='actionsMenuButton'
                  className='dropdown-toggle btn-ghost-secondary'
                  variant='link'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'><strong>Actions</strong></Button>
                <div className='dropdown-menu' aria-labelledby='actionsMenuButton'>
                  <EditViewButton
                    canModify={canModify}
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setCreateEditSupplier({ supplier })} />
                  <PermissionButton
                    permission="otherEntitiesModify"
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setSupplierToRemove(supplier)}>
                    Remove
                  </PermissionButton>
                </div>
              </div>
            </td>
          </tr>)}
        {(loadingRows || visibleSuppliers.length === 0) &&
          <tr>
            <td colSpan={3} className='text-center text-muted py-3'>
              {loadingRows ? <CenteredSpinner /> : 'No suppliers to show'}
            </td>
          </tr>}
      </tbody>
    </Table>
  </Container>
};

export default SupplierPage;
